import React, { useState, useEffect } from 'react';
import { ShoppingCart, X, Plus, Minus, Package, Tag, ChevronDown, ChevronUp, Image, Star } from 'lucide-react';

const C = {
  bg:'#070D0A', card:'#0E1812', border:'#1C2E22',
  gold:'#C9A84C', goldLt:'#E8C97A', text:'#EDE8DC',
  muted:'rgba(237,232,220,0.5)', red:'#ef4444',
  green:'#22c55e', input:'#111A14',
};

interface Variant { id:string; variantType:string; variantValue:string; stockQty:number; priceAdj:number; isActive:boolean; }
interface Product {
  id:string; name:string; slug:string; description:string; shortDesc:string;
  category:string; price:number; comparePrice:number|null; currency:string;
  imageUrls:string[]; badge:string; sku:string; stockQty:number;
  isActive:boolean; isFeatured:boolean; isDigital:boolean; allowsVariants:boolean;
  salesCount:number; variants:Variant[];
}
interface CartItem { product:Product; qty:number; selectedVariants:Record<string,string>; finalPrice:number; }

interface PaystackStoreProps {
  currentUser?: { email?:string; displayName?:string } | null;
}

export default function PaystackStore({ currentUser }: PaystackStoreProps) {
  const [products, setProducts]         = useState<Product[]>([]);
  const [categories, setCategories]     = useState<string[]>([]);
  const [loading, setLoading]           = useState(true);
  const [selectedCat, setSelectedCat]   = useState('All');
  const [cart, setCart]                 = useState<CartItem[]>([]);
  const [cartOpen, setCartOpen]         = useState(false);
  const [expandedId, setExpandedId]     = useState<string|null>(null);
  const [selectedVariants, setSelectedVariants] = useState<Record<string,Record<string,string>>>({});

  // Checkout state
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [buyerName, setBuyerName]       = useState(currentUser?.displayName||'');
  const [buyerEmail, setBuyerEmail]     = useState(currentUser?.email||'');
  const [buyerPhone, setBuyerPhone]     = useState('');
  const [delivery, setDelivery]         = useState<'palace_pickup'|'home_delivery'>('palace_pickup');
  const [deliveryAddr, setDeliveryAddr] = useState('');
  const [processing, setProcessing]     = useState(false);
  const [toast, setToast]               = useState<{msg:string;type:'ok'|'err'}|null>(null);

  const showToast = (msg:string, type:'ok'|'err'='ok') => {
    setToast({msg,type}); setTimeout(()=>setToast(null),3500);
  };

  useEffect(()=>{
    fetch('/api/store/public')
      .then(r=>r.json())
      .then(d=>{ if(d.success){ setProducts(d.products||[]); setCategories(['All',...(d.categories||[])]); }})
      .catch(()=>showToast('Failed to load store','err'))
      .finally(()=>setLoading(false));
  },[]);

  const filtered = selectedCat==='All' ? products : products.filter(p=>p.category===selectedCat);
  const featured = products.filter(p=>p.isFeatured).slice(0,3);

  const getVariantKey = (productId:string, type:string) => selectedVariants[productId]?.[type]||'';

  const setVariant = (productId:string, type:string, value:string) => {
    setSelectedVariants(prev=>({ ...prev, [productId]:{ ...(prev[productId]||{}), [type]:value } }));
  };

  const calcPrice = (p:Product, variants:Record<string,string>) => {
    let price = p.price;
    p.variants.forEach(v=>{ if(variants[v.variantType]===v.variantValue) price += v.priceAdj; });
    return price;
  };

  const addToCart = (p:Product) => {
    const vars = selectedVariants[p.id]||{};
    // Check required variants selected
    const variantTypes = [...new Set(p.variants.map(v=>v.variantType))];
    for (const type of variantTypes) {
      if (!vars[type]) { showToast(`Please select a ${type}`,'err'); return; }
    }
    const finalPrice = calcPrice(p, vars);
    setCart(prev=>{
      const existing = prev.findIndex(c=>c.product.id===p.id && JSON.stringify(c.selectedVariants)===JSON.stringify(vars));
      if (existing>=0) {
        const next = [...prev];
        next[existing] = { ...next[existing], qty: next[existing].qty+1 };
        return next;
      }
      return [...prev, { product:p, qty:1, selectedVariants:vars, finalPrice }];
    });
    showToast(`${p.name} added to cart`);
  };

  const updateQty = (idx:number, delta:number) => {
    setCart(prev=>{
      const next = [...prev];
      next[idx] = { ...next[idx], qty: Math.max(1, next[idx].qty+delta) };
      return next;
    });
  };

  const removeFromCart = (idx:number) => setCart(prev=>prev.filter((_,i)=>i!==idx));

  const cartTotal  = cart.reduce((s,c)=>s+(c.finalPrice*c.qty),0);
  const cartCount  = cart.reduce((s,c)=>s+c.qty,0);
  const deliveryFee = delivery==='home_delivery' ? 2000 : 0;
  const grandTotal  = cartTotal + deliveryFee;

  const checkout = async () => {
    if (!buyerName||!buyerEmail) return showToast('Name and email required','err');
    if (delivery==='home_delivery'&&!deliveryAddr) return showToast('Delivery address required','err');
    setProcessing(true);
    try {
      const r = await fetch('/api/store/checkout', {
        method:'POST', headers:{'Content-Type':'application/json'},
        body: JSON.stringify({
          buyerName, buyerEmail, buyerPhone, delivery, deliveryAddr,
          items: cart.map(c=>({ productId:c.product.id, name:c.product.name,
            qty:c.qty, price:c.finalPrice, variants:c.selectedVariants })),
          total: grandTotal,
        })
      });
      const d = await r.json();
      if (d.success && d.authorizationUrl) {
        window.location.href = d.authorizationUrl;
      } else if (d.success && d.reference) {
        showToast('Order placed!'); setCart([]); setCheckoutOpen(false);
      } else {
        showToast(d.error||'Checkout failed','err');
      }
    } catch(e:any){ showToast(e.message,'err'); }
    setProcessing(false);
  };

  const formatNGN = (n:number) => `₦${n.toLocaleString()}`;
  const iS: React.CSSProperties = {
    width:'100%', padding:'8px 10px', borderRadius:6,
    background:C.input, border:`1px solid ${C.border}`,
    color:C.text, fontSize:13, outline:'none', boxSizing:'border-box',
  };

  if (loading) return (
    <div style={{ background:C.bg, minHeight:'100vh', display:'flex', alignItems:'center', justifyContent:'center' }}>
      <div style={{ color:C.muted, fontSize:14 }}>Loading Festival Store...</div>
    </div>
  );

  return (
    <div style={{ background:C.bg, minHeight:'100vh', fontFamily:'Plus Jakarta Sans, sans-serif' }}>

      {/* Toast */}
      {toast && (
        <div style={{ position:'fixed', top:20, right:20, zIndex:9999, background:toast.type==='ok'?C.green:C.red,
                       color:'#fff', borderRadius:8, padding:'10px 18px', fontSize:13, fontWeight:700 }}>
          {toast.type==='ok'?'✅':'❌'} {toast.msg}
        </div>
      )}

      {/* Header */}
      <div style={{ background:'linear-gradient(135deg,#0A1A0F,#0E1812)', borderBottom:`1px solid ${C.border}`,
                     padding:'24px 20px', position:'sticky', top:0, zIndex:100 }}>
        <div style={{ maxWidth:900, margin:'0 auto', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <div>
            <h1 style={{ fontFamily:'Cinzel, serif', color:C.gold, margin:0, fontSize:22 }}>Festival Store</h1>
            <p style={{ color:C.muted, fontSize:12, margin:'4px 0 0' }}>Uzairue Kingdom Annual Festival 2026 — Official Merchandise</p>
          </div>
          <button onClick={()=>setCartOpen(true)}
            style={{ position:'relative', background:C.gold, border:'none', borderRadius:8,
                      padding:'10px 16px', cursor:'pointer', display:'flex', alignItems:'center', gap:8 }}>
            <ShoppingCart size={18} color="#000"/>
            <span style={{ color:'#000', fontWeight:800, fontSize:14 }}>{formatNGN(cartTotal)}</span>
            {cartCount>0 && (
              <span style={{ position:'absolute', top:-6, right:-6, background:C.red, color:'#fff',
                              borderRadius:'50%', width:18, height:18, fontSize:10, fontWeight:800,
                              display:'flex', alignItems:'center', justifyContent:'center' }}>{cartCount}</span>
            )}
          </button>
        </div>
      </div>

      <div style={{ maxWidth:900, margin:'0 auto', padding:'20px 16px' }}>

        {/* Empty state */}
        {products.length===0 && (
          <div style={{ background:C.card, border:`1px dashed ${C.border}`, borderRadius:12,
                         padding:60, textAlign:'center' }}>
            <Package size={48} color={C.muted} style={{ margin:'0 auto 16px' }}/>
            <div style={{ color:C.gold, fontFamily:'Cinzel, serif', fontSize:18, marginBottom:8 }}>Store Coming Soon</div>
            <div style={{ color:C.muted, fontSize:13 }}>Festival merchandise and tickets will be available here shortly.</div>
          </div>
        )}

        {/* Featured */}
        {featured.length>0 && (
          <div style={{ marginBottom:28 }}>
            <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:14 }}>
              <Star size={16} color={C.gold}/>
              <span style={{ color:C.gold, fontFamily:'Cinzel, serif', fontSize:14, fontWeight:700 }}>Featured Items</span>
            </div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:14 }}>
              {featured.map(p=><ProductCard key={p.id} p={p} onAdd={addToCart}
                expanded={expandedId===p.id} onToggle={()=>setExpandedId(expandedId===p.id?null:p.id)}
                selectedVariants={selectedVariants[p.id]||{}} onVariantSelect={(t,v)=>setVariant(p.id,t,v)}
                calcPrice={calcPrice} formatNGN={formatNGN}/>)}
            </div>
          </div>
        )}

        {/* Category filter */}
        {categories.length>1 && (
          <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:20 }}>
            {categories.map(cat=>(
              <button key={cat} onClick={()=>setSelectedCat(cat)}
                style={{ padding:'6px 14px', borderRadius:20, fontSize:12, fontWeight:700, cursor:'pointer',
                          background:selectedCat===cat?C.gold:'transparent', color:selectedCat===cat?'#000':C.muted,
                          border:`1px solid ${selectedCat===cat?C.gold:C.border}` }}>
                {cat}
              </button>
            ))}
          </div>
        )}

        {/* Product grid */}
        {filtered.length>0 && (
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(240px,1fr))', gap:14 }}>
            {filtered.map(p=><ProductCard key={p.id} p={p} onAdd={addToCart}
              expanded={expandedId===p.id} onToggle={()=>setExpandedId(expandedId===p.id?null:p.id)}
              selectedVariants={selectedVariants[p.id]||{}} onVariantSelect={(t,v)=>setVariant(p.id,t,v)}
              calcPrice={calcPrice} formatNGN={formatNGN}/>)}
          </div>
        )}
      </div>

      {/* CART SIDEBAR */}
      {cartOpen && (
        <div style={{ position:'fixed', inset:0, zIndex:500 }}>
          <div onClick={()=>setCartOpen(false)}
            style={{ position:'absolute', inset:0, background:'rgba(0,0,0,0.7)' }}/>
          <div style={{ position:'absolute', right:0, top:0, bottom:0, width:'min(420px,100vw)',
                         background:C.card, borderLeft:`1px solid ${C.border}`, display:'flex', flexDirection:'column' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                           padding:'16px 20px', borderBottom:`1px solid ${C.border}` }}>
              <h3 style={{ fontFamily:'Cinzel, serif', color:C.gold, margin:0, fontSize:16 }}>
                Shopping Cart ({cartCount})
              </h3>
              <button onClick={()=>setCartOpen(false)} style={{ background:'none', border:'none', color:C.muted, cursor:'pointer' }}>
                <X size={20}/>
              </button>
            </div>

            <div style={{ flex:1, overflowY:'auto', padding:'16px 20px' }}>
              {cart.length===0
                ? <div style={{ textAlign:'center', color:C.muted, padding:40 }}>Your cart is empty</div>
                : cart.map((item,i)=>(
                    <div key={i} style={{ background:'#0A120D', border:`1px solid ${C.border}`,
                                           borderRadius:8, padding:12, marginBottom:10 }}>
                      <div style={{ display:'flex', gap:10, alignItems:'flex-start' }}>
                        <div style={{ width:44, height:44, borderRadius:6, background:C.border, overflow:'hidden', flexShrink:0 }}>
                          {item.product.imageUrls?.[0]
                            ? <img src={item.product.imageUrls[0]} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                            : <Package size={16} color={C.muted} style={{ margin:'14px auto', display:'block' }}/>}
                        </div>
                        <div style={{ flex:1, minWidth:0 }}>
                          <div style={{ color:C.text, fontSize:13, fontWeight:700, marginBottom:2 }}>{item.product.name}</div>
                          {Object.entries(item.selectedVariants).map(([t,v])=>(
                            <span key={t} style={{ color:C.muted, fontSize:10, marginRight:8, textTransform:'capitalize' }}>{t}: {v}</span>
                          ))}
                          <div style={{ color:C.gold, fontSize:13, fontWeight:700, marginTop:4 }}>
                            {formatNGN(item.finalPrice * item.qty)}
                          </div>
                        </div>
                        <button onClick={()=>removeFromCart(i)}
                          style={{ background:'none', border:'none', color:C.red, cursor:'pointer', padding:2 }}>
                          <X size={14}/>
                        </button>
                      </div>
                      <div style={{ display:'flex', alignItems:'center', gap:10, marginTop:10 }}>
                        <button onClick={()=>updateQty(i,-1)}
                          style={{ background:C.border, border:'none', borderRadius:4, width:26, height:26, cursor:'pointer', color:C.text, display:'flex', alignItems:'center', justifyContent:'center' }}>
                          <Minus size={12}/>
                        </button>
                        <span style={{ color:C.text, fontSize:13, fontWeight:700, minWidth:20, textAlign:'center' }}>{item.qty}</span>
                        <button onClick={()=>updateQty(i,1)}
                          style={{ background:C.border, border:'none', borderRadius:4, width:26, height:26, cursor:'pointer', color:C.text, display:'flex', alignItems:'center', justifyContent:'center' }}>
                          <Plus size={12}/>
                        </button>
                        <span style={{ color:C.muted, fontSize:11, marginLeft:'auto' }}>{formatNGN(item.finalPrice)} each</span>
                      </div>
                    </div>
                  ))}
            </div>

            {cart.length>0 && (
              <div style={{ padding:'16px 20px', borderTop:`1px solid ${C.border}` }}>
                <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8 }}>
                  <span style={{ color:C.muted, fontSize:13 }}>Subtotal</span>
                  <span style={{ color:C.text, fontSize:13, fontWeight:700 }}>{formatNGN(cartTotal)}</span>
                </div>
                <button onClick={()=>{ setCartOpen(false); setCheckoutOpen(true); }}
                  style={{ width:'100%', background:C.gold, border:'none', borderRadius:8, padding:'12px',
                            color:'#000', fontWeight:800, fontSize:14, cursor:'pointer', fontFamily:'Cinzel, serif' }}>
                  Proceed to Checkout
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* CHECKOUT MODAL */}
      {checkoutOpen && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', zIndex:600,
                       display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12,
                         width:'100%', maxWidth:500, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                           padding:'16px 20px', borderBottom:`1px solid ${C.border}` }}>
              <h3 style={{ fontFamily:'Cinzel, serif', color:C.gold, margin:0, fontSize:16 }}>Complete Order</h3>
              <button onClick={()=>setCheckoutOpen(false)} style={{ background:'none', border:'none', color:C.muted, cursor:'pointer' }}>
                <X size={18}/>
              </button>
            </div>
            <div style={{ padding:20 }}>

              {/* Order summary */}
              <div style={{ background:'#0A120D', borderRadius:8, padding:12, marginBottom:16 }}>
                {cart.map((item,i)=>(
                  <div key={i} style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:12 }}>
                    <span style={{ color:C.muted }}>{item.product.name} ×{item.qty}</span>
                    <span style={{ color:C.text, fontWeight:700 }}>{formatNGN(item.finalPrice*item.qty)}</span>
                  </div>
                ))}
                <div style={{ borderTop:`1px solid ${C.border}`, paddingTop:8, marginTop:8,
                               display:'flex', justifyContent:'space-between' }}>
                  <span style={{ color:C.muted, fontSize:12 }}>Delivery</span>
                  <span style={{ color:C.text, fontSize:12 }}>{delivery==='home_delivery'?formatNGN(2000):'Free (Palace Pickup)'}</span>
                </div>
                <div style={{ display:'flex', justifyContent:'space-between', marginTop:6 }}>
                  <span style={{ color:C.gold, fontWeight:800, fontSize:14 }}>Total</span>
                  <span style={{ color:C.gold, fontWeight:800, fontSize:14 }}>{formatNGN(grandTotal)}</span>
                </div>
              </div>

              {/* Buyer details */}
              <div style={{ display:'grid', gap:10, marginBottom:14 }}>
                <div>
                  <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:700 }}>Full Name *</div>
                  <input value={buyerName} onChange={e=>setBuyerName(e.target.value)} placeholder="Your full name" style={{
                    width:'100%', padding:'8px 10px', borderRadius:6, background:C.input,
                    border:`1px solid ${C.border}`, color:C.text, fontSize:13, outline:'none', boxSizing:'border-box' as any
                  }}/>
                </div>
                <div>
                  <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:700 }}>Email *</div>
                  <input value={buyerEmail} onChange={e=>setBuyerEmail(e.target.value)} placeholder="your@email.com" style={{
                    width:'100%', padding:'8px 10px', borderRadius:6, background:C.input,
                    border:`1px solid ${C.border}`, color:C.text, fontSize:13, outline:'none', boxSizing:'border-box' as any
                  }}/>
                </div>
                <div>
                  <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:700 }}>Phone</div>
                  <input value={buyerPhone} onChange={e=>setBuyerPhone(e.target.value)} placeholder="+234..." style={{
                    width:'100%', padding:'8px 10px', borderRadius:6, background:C.input,
                    border:`1px solid ${C.border}`, color:C.text, fontSize:13, outline:'none', boxSizing:'border-box' as any
                  }}/>
                </div>
                <div>
                  <div style={{ fontSize:11, color:C.muted, marginBottom:6, fontWeight:700 }}>Delivery Option</div>
                  <div style={{ display:'flex', gap:10 }}>
                    {([['palace_pickup','Palace Pickup (Free)'],['home_delivery','Home Delivery (+₦2,000)']] as const).map(([val,label])=>(
                      <label key={val} style={{ display:'flex', alignItems:'center', gap:6, cursor:'pointer', fontSize:12, color:C.text }}>
                        <input type="radio" name="delivery" value={val} checked={delivery===val} onChange={()=>setDelivery(val)}
                          style={{ accentColor:C.gold }}/>
                        {label}
                      </label>
                    ))}
                  </div>
                </div>
                {delivery==='home_delivery' && (
                  <div>
                    <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:700 }}>Delivery Address *</div>
                    <textarea value={deliveryAddr} onChange={e=>setDeliveryAddr(e.target.value)}
                      placeholder="Full delivery address..." rows={2} style={{
                        width:'100%', padding:'8px 10px', borderRadius:6, background:C.input,
                        border:`1px solid ${C.border}`, color:C.text, fontSize:13, outline:'none',
                        boxSizing:'border-box' as any, resize:'vertical'
                      }}/>
                  </div>
                )}
              </div>

              <button onClick={checkout} disabled={processing}
                style={{ width:'100%', background:processing?C.border:C.gold, border:'none', borderRadius:8,
                          padding:'13px', color:processing?C.muted:'#000', fontWeight:800, fontSize:14,
                          cursor:processing?'not-allowed':'pointer', fontFamily:'Cinzel, serif' }}>
                {processing ? 'Processing...' : `Pay ${formatNGN(grandTotal)} via Paystack`}
              </button>
              <p style={{ color:C.muted, fontSize:11, textAlign:'center', marginTop:10 }}>
                Secured by Paystack · Your payment is encrypted and protected
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Product Card Component ────────────────────────────────────────────────────
function ProductCard({ p, onAdd, expanded, onToggle, selectedVariants, onVariantSelect, calcPrice, formatNGN }:
  { p:Product; onAdd:(p:Product)=>void; expanded:boolean; onToggle:()=>void;
    selectedVariants:Record<string,string>; onVariantSelect:(t:string,v:string)=>void;
    calcPrice:(p:Product,v:Record<string,string>)=>number; formatNGN:(n:number)=>string; }) {

  const variantTypes = [...new Set(p.variants.map(v=>v.variantType))];
  const currentPrice = calcPrice(p, selectedVariants);

  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:10, overflow:'hidden',
                   display:'flex', flexDirection:'column' }}>
      {/* Image */}
      <div style={{ height:180, background:'#0A120D', display:'flex', alignItems:'center', justifyContent:'center',
                     position:'relative', overflow:'hidden' }}>
        {p.imageUrls?.[0]
          ? <img src={p.imageUrls[0]} alt={p.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
          : <Package size={48} color={C.muted}/>}
        {p.badge && (
          <span style={{ position:'absolute', top:10, left:10, background:C.gold, color:'#000',
                          fontSize:10, fontWeight:800, padding:'3px 10px', borderRadius:20 }}>{p.badge}</span>
        )}
        {p.comparePrice && p.comparePrice > p.price && (
          <span style={{ position:'absolute', top:10, right:10, background:C.red, color:'#fff',
                          fontSize:10, fontWeight:800, padding:'3px 8px', borderRadius:20 }}>
            SALE
          </span>
        )}
      </div>

      {/* Info */}
      <div style={{ padding:'12px 14px', flex:1, display:'flex', flexDirection:'column', gap:6 }}>
        <div style={{ fontSize:10, color:C.muted, textTransform:'uppercase', letterSpacing:1 }}>{p.category}</div>
        <div style={{ color:C.text, fontWeight:700, fontSize:14, lineHeight:1.3 }}>{p.name}</div>
        {p.shortDesc && <div style={{ color:C.muted, fontSize:11, lineHeight:1.5 }}>{p.shortDesc}</div>}

        <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:4 }}>
          <span style={{ color:C.gold, fontWeight:800, fontSize:16 }}>{formatNGN(currentPrice)}</span>
          {p.comparePrice && p.comparePrice>p.price && (
            <span style={{ color:C.muted, fontSize:12, textDecoration:'line-through' }}>{formatNGN(p.comparePrice)}</span>
          )}
        </div>

        {!p.isDigital && (
          <div style={{ fontSize:11, color:p.stockQty<=5?C.red:C.muted }}>
            {p.stockQty<=5&&p.stockQty>0?`⚠️ Only ${p.stockQty} left`:p.stockQty===0?'Out of Stock':'In Stock'}
          </div>
        )}

        {/* Variants */}
        {variantTypes.length>0 && (
          <div style={{ marginTop:6 }}>
            {variantTypes.map(type=>{
              const options = p.variants.filter(v=>v.variantType===type&&v.isActive);
              return (
                <div key={type} style={{ marginBottom:8 }}>
                  <div style={{ fontSize:10, color:C.muted, textTransform:'capitalize', marginBottom:4, fontWeight:700 }}>{type}</div>
                  <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                    {options.map(v=>{
                      const sel = selectedVariants[type]===v.variantValue;
                      const outOfStock = !p.isDigital && v.stockQty===0;
                      return (
                        <button key={v.id} disabled={outOfStock}
                          onClick={()=>onVariantSelect(type,v.variantValue)}
                          style={{ padding:'4px 10px', borderRadius:6, fontSize:11, fontWeight:700,
                                    cursor:outOfStock?'not-allowed':'pointer', opacity:outOfStock?0.4:1,
                                    background:sel?C.gold:'transparent', color:sel?'#000':C.muted,
                                    border:`1px solid ${sel?C.gold:C.border}`,
                                    textDecoration:outOfStock?'line-through':'none' }}>
                          {v.variantValue}
                          {v.priceAdj!==0&&<span style={{ marginLeft:3, fontSize:9 }}>({v.priceAdj>0?'+':''}{formatNGN(v.priceAdj)})</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Description toggle */}
        {p.description && (
          <button onClick={onToggle}
            style={{ background:'none', border:'none', color:C.muted, cursor:'pointer',
                      fontSize:11, display:'flex', alignItems:'center', gap:4, padding:0, marginTop:4 }}>
            {expanded?<ChevronUp size={12}/>:<ChevronDown size={12}/>}
            {expanded?'Less info':'More info'}
          </button>
        )}
        {expanded && p.description && (
          <div style={{ color:C.muted, fontSize:11, lineHeight:1.6, marginTop:4 }}>{p.description}</div>
        )}
      </div>

      {/* Add to cart */}
      <div style={{ padding:'0 14px 14px' }}>
        <button onClick={()=>onAdd(p)} disabled={!p.isDigital&&p.stockQty===0}
          style={{ width:'100%', background:(!p.isDigital&&p.stockQty===0)?C.border:C.gold,
                    border:'none', borderRadius:8, padding:'10px', color:(!p.isDigital&&p.stockQty===0)?C.muted:'#000',
                    fontWeight:800, fontSize:13, cursor:(!p.isDigital&&p.stockQty===0)?'not-allowed':'pointer',
                    display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
          <ShoppingCart size={14}/>
          {!p.isDigital&&p.stockQty===0?'Out of Stock':'Add to Cart'}
        </button>
      </div>
    </div>
  );
}
